import { Link } from 'react-router-dom'
import { getAvatarColor, getInitials } from '@/lib/avatar'
import { Badge } from '@/components/ui/badge'
import { getStatusLabel, getStatusVariant } from '@/lib/status-config'
import { RECOMMENDATION_LABEL } from '@/lib/gng-score'
import { collaborationStatusConfig } from '@/lib/section-status'
import { LIFECYCLE_LABELS, OUTCOME_SHORT_LABELS, STAGE_LABELS, isBidOutcome, isBidStage } from '@/lib/bid-lifecycle'
import type { CollaborationStatusKey } from '@/lib/section-status'
import {
  type ActivityRecord,
  ACTION_SUFFIXES,
  getTargetName,
  getActionLabel,
  getLifecycleChangeLabel,
  getStatusChangeLabel,
  getActivityLink,
  formatRelativeTime,
  formatClockTime,
} from './activity-labels'

// Re-export so consumers can import the type from here
export type { ActivityRecord }

// ===================
// Status-pill helpers
// ===================

function BidStatusPills({ metadata }: { metadata: Record<string, unknown> }) {
  const from = metadata['from'] as string | undefined
  const to = metadata['to'] as string | undefined
  if (!from && !to) return null
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      {from && (
        <Badge variant={getStatusVariant(from)} className="px-1.5 py-0 text-[10px]">
          {getStatusLabel(from)}
        </Badge>
      )}
      {from && to && <span className="text-meta">→</span>}
      {to && (
        <Badge variant={getStatusVariant(to)} className="px-1.5 py-0 text-[10px]">
          {getStatusLabel(to)}
        </Badge>
      )}
    </span>
  )
}

function ReviewStatusPill({ status }: { status: string }) {
  const cfg = collaborationStatusConfig[status as CollaborationStatusKey]
  if (!cfg) return null
  return (
    <Badge variant={cfg.variant} className="px-1.5 py-0 text-[10px]">
      {cfg.label}
    </Badge>
  )
}

const GNG_DECISION_CONFIG: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' }> = {
  go: { label: RECOMMENDATION_LABEL.go, variant: 'success' },
  conditional_go: { label: RECOMMENDATION_LABEL.conditional_go, variant: 'warning' },
  no_go: { label: RECOMMENDATION_LABEL.no_go, variant: 'destructive' },
}

function GoNoGoDecisionPill({ decision }: { decision: string }) {
  const cfg = GNG_DECISION_CONFIG[decision]
  if (!cfg) return null
  return (
    <Badge variant={cfg.variant} className="px-1.5 py-0 text-[10px]">
      {cfg.label}
    </Badge>
  )
}

// R4: the same rule as the stage pill. Won is the one coloured result; every
// other outcome reads neutral.
function BidOutcomePill({ outcome }: { outcome: string }) {
  if (!isBidOutcome(outcome)) return null
  return (
    <Badge variant={outcome === 'won' ? 'success' : 'secondary'} dot={false} className="px-1.5 py-0 text-[10px]">
      {OUTCOME_SHORT_LABELS[outcome]}
    </Badge>
  )
}

function SubmittedPill() {
  return (
    <Badge variant="secondary" dot={false} className="px-1.5 py-0 text-[10px]">
      {LIFECYCLE_LABELS.lodged}
    </Badge>
  )
}

/**
 * Pills for an old-vocabulary status change, matching its R4 label: "Submitted"
 * for a submission, the result for won / lost / no go, nothing for a stage move
 * (the label already names it), and the raw before/after pills otherwise.
 */
function StatusChangeExtra({ metadata }: { metadata: Record<string, unknown> }) {
  const to = metadata['to']
  if (to === 'submitted') return <SubmittedPill />
  if (to === 'won' || to === 'lost' || to === 'no_go') return <BidOutcomePill outcome={to} />
  if (getStatusChangeLabel(metadata) !== null) return null
  return <BidStatusPills metadata={metadata} />
}

function StagePill({ stage }: { stage: unknown }) {
  if (!isBidStage(stage)) return null
  return (
    <Badge variant="secondary" dot={false} className="px-1.5 py-0 text-[10px]">
      {STAGE_LABELS[stage]}
    </Badge>
  )
}

/** "Plan → Write" for a stage move. */
function StageChangePills({ metadata }: { metadata: Record<string, unknown> }) {
  const from = metadata['from']
  const to = metadata['to']
  if (!isBidStage(from) && !isBidStage(to)) return null
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      <StagePill stage={from} />
      {isBidStage(from) && isBidStage(to) && <span className="text-meta">→</span>}
      <StagePill stage={to} />
    </span>
  )
}

/** "A", "A and B", "A, B and C" */
function formatNameList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** "mentioned Sarah Kim and Eval Harness" — @mentions ride on the comment row. */
function MentionNote({ metadata }: { metadata: Record<string, unknown> }) {
  const raw = metadata['mentionedNames']
  if (!Array.isArray(raw)) return null
  // Metadata is untyped JSON from the activity row — skip anything that isn't a
  // name rather than rendering "[object Object]".
  const names = raw.filter((n): n is string => typeof n === 'string' && n.trim().length > 0)
  if (names.length === 0) return null
  return <span className="text-muted-foreground">— mentioned {formatNameList(names)}</span>
}

// ===================
// Main component
// ===================

interface ActivityRowProps {
  record: ActivityRecord
  currentUserId: string
  /** bidId — if not on record, falls back to record.bidId */
  bidId?: string
  /**
   * How to stamp the row.
   *
   * `relative` ("27m ago") suits an ungrouped feed. `clock` ("11:48 pm") is for
   * rows under a day heading: the heading already says which day, and an
   * elapsed-time stamp measured across local midnight contradicts it — an event
   * 27 minutes old sitting under "Yesterday" reading "27m ago".
   */
  timestampFormat?: 'relative' | 'clock'
}

export function ActivityRow({
  record,
  currentUserId,
  bidId: bidIdProp,
  timestampFormat = 'relative',
}: ActivityRowProps) {
  const bidId = bidIdProp ?? record.bidId ?? ''
  const isCurrentUser = record.userId === currentUserId
  const actorName = isCurrentUser ? 'You' : (record.userName?.split(' ')[0] ?? 'Someone')

  // Accept-all win themes log ONE aggregated row carrying `count` (no name).
  // Render a batch phrase and suppress the (absent) target instead of the
  // singular label. Keyed on count presence, not `count > 1`, so count===1
  // doesn't fall through to a dangling "added the win theme".
  const themeCount =
    record.action === 'win_theme_added'
      ? (record.metadata['count'] as number | undefined)
      : undefined
  const isThemeBatch = themeCount != null

  const target = isThemeBatch ? null : getTargetName(record)
  const actionLabel = isThemeBatch
    ? `accepted ${themeCount} AI-suggested win theme${themeCount === 1 ? '' : 's'}`
    : record.action === 'bid_lifecycle_changed'
      ? getLifecycleChangeLabel(record.metadata)
      : (record.action === 'bid_status_changed' ? getStatusChangeLabel(record.metadata) : null) ??
        getActionLabel(record.action, target !== null, isCurrentUser)
  const suffix = target ? ACTION_SUFFIXES[record.action] : undefined

  const link = getActivityLink(bidId, record)
  const avatarColors = getAvatarColor(record.userId)

  // Inline metadata for specific actions
  const inlineExtra = (() => {
    if (record.action === 'bid_status_changed') {
      return <StatusChangeExtra metadata={record.metadata} />
    }
    if (record.action === 'bid_stage_changed') {
      return <StageChangePills metadata={record.metadata} />
    }
    if (record.action === 'bid_lifecycle_changed') {
      const to = record.metadata['to']
      if (to === 'active') return <StagePill stage={record.metadata['toStage']} />
      return null
    }
    if (record.action === 'go_no_go_decided') {
      const decision = record.metadata['decision'] as string | undefined
      if (decision) return <GoNoGoDecisionPill decision={decision} />
    }
    if (record.action === 'bid_outcome_recorded') {
      const outcome = record.metadata['outcome'] as string | undefined
      if (outcome) return <BidOutcomePill outcome={outcome} />
    }
    if (
      record.action === 'artifact_review_approved' ||
      record.action === 'artifact_review_changes_requested' ||
      record.action === 'artifact_review_cancelled' ||
      record.action === 'review_approved' ||
      record.action === 'changes_requested'
    ) {
      const status = record.metadata['status'] as string | undefined
      if (status) return <ReviewStatusPill status={status} />
    }
    if (record.action === 'comment_added' || record.action === 'comment_replied') {
      return <MentionNote metadata={record.metadata} />
    }
    if (record.action === 'bid_updated') {
      const changed = record.metadata['changedFields'] as string[] | undefined
      if (changed?.length) {
        return (
          <span className="text-muted-foreground">
            {changed.join(', ')}
          </span>
        )
      }
    }
    return null
  })()

  const avatar = record.userAvatarUrl ? (
    <img
      src={record.userAvatarUrl}
      className="mt-0.5 h-6 w-6 shrink-0 rounded-full"
      alt=""
    />
  ) : (
    <div
      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-medium ${avatarColors.bg} ${avatarColors.text}`}
    >
      {getInitials(record.userName)}
    </div>
  )

  const content = (
    <>
      {avatar}
      <div className="min-w-0 flex-1">
        {/* A div, not a p: `inlineExtra` renders Badge, which is a div, and a
            div inside a p is invalid HTML — React logs validateDOMNesting and
            the browser silently closes the paragraph early. Changed here rather
            than in Badge, which is used across the app. */}
        <div className="text-[13px]">
          <span className="font-medium">{actorName}</span>{' '}
          {actionLabel}
          {target && (
            <> <span className="font-medium">{target}</span></>
          )}
          {suffix && <> {suffix}</>}
          {inlineExtra && <> {inlineExtra}</>}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {timestampFormat === 'clock'
            ? formatClockTime(record.createdAt)
            : formatRelativeTime(record.createdAt)}
        </p>
      </div>
    </>
  )

  const baseClass = 'flex items-start gap-2.5 py-2.5'

  if (link) {
    return (
      <Link
        to={link}
        className={`${baseClass} -mx-2 px-2 rounded-md hover:bg-accent/50 transition-colors`}
      >
        {content}
      </Link>
    )
  }

  return (
    <div className={baseClass}>
      {content}
    </div>
  )
}
